/**
 * Serviço para lidar com campos computados em formulários dinâmicos
 * Suporta formatações automáticas como AUTO_GENERATE_PASSWORD, PREFIXO_TIMESTAMP, etc.
 */

/**
 * Gera uma senha aleatória
 * @param {number} length - Tamanho da senha
 * @returns {string} - Senha gerada
 */
export function gerarSenhaAleatoria(length = 8) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
  let senha = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    senha += charset[randomIndex];
  }
  return senha;
}

/**
 * Retorna a data/hora atual formatada no padrão brasileiro DD/MM/AAAA HH:MM:SS
 * @returns {string} - Data/hora formatada
 */
export function gerarDataHoraBrasil() {
  const agora = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const dia = pad(agora.getDate());
  const mes = pad(agora.getMonth() + 1);
  const ano = agora.getFullYear();
  const hora = pad(agora.getHours());
  const minuto = pad(agora.getMinutes());
  const segundo = pad(agora.getSeconds());
  return `${dia}${mes}${ano}${hora}${minuto}${segundo}`;
}

/**
 * Gera um código com prefixo baseado no valor do campo dependente + data/hora brasileira
 * @param {string} prefixValue - Valor do campo dependente (ex: "P" ou "I")
 * @returns {string} - Código gerado (ex: "P_06/04/2026 14:30:45")
 */
export function gerarCodigoComPrefixoTimestamp(prefixValue) {
  if (!prefixValue) {
    return '';
  }
  return `${prefixValue}${gerarDataHoraBrasil()}`;
}

/**
 * Gera um UUID v4
 * @returns {string} - UUID gerado
 */
export function gerarUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Formata um valor baseado no tipo de formatação especificado
 * @param {string} format - Tipo de formatação (AUTO_GENERATE_PASSWORD, PREFIXO_TIMESTAMP, etc.)
 * @param {any} dependsOnValue - Valor do campo do qual depende
 * @param {object} options - Opções adicionais de formatação
 * @returns {string} - Valor formatado
 */
export function formatarCampoComputado(format, dependsOnValue = null, options = {}) {
  switch (format) {
    case 'AUTO_GENERATE_PASSWORD':
      return gerarSenhaAleatoria(options.length || 8);
    
    case 'PREFIXO_TIMESTAMP':
      return gerarCodigoComPrefixoTimestamp(dependsOnValue);
    
    case 'TIMESTAMP':
      return gerarDataHoraBrasil();
    
    case 'UUID':
      return gerarUUID();
    
    case 'UPPERCASE':
      return dependsOnValue ? dependsOnValue.toString().toUpperCase() : '';
    
    case 'LOWERCASE':
      return dependsOnValue ? dependsOnValue.toString().toLowerCase() : '';
    
    case 'CAPITALIZE':
      return dependsOnValue 
        ? dependsOnValue.toString().charAt(0).toUpperCase() + dependsOnValue.toString().slice(1).toLowerCase()
        : '';
    
    case 'COPY':
      // Simplesmente copia o valor do campo dependente
      return dependsOnValue || '';
    
    case 'CONCAT': {
      // Concatena o valor com um prefixo/sufixo
      const prefix = options.prefix || '';
      const suffix = options.suffix || '';
      return `${prefix}${dependsOnValue || ''}${suffix}`;
    }
    
    default:
      return dependsOnValue || '';
  }
}

/**
 * Processa um campo computado e retorna o valor formatado
 * @param {object} campo - Definição do campo computado
 * @param {object} formValues - Valores atuais do formulário
 * @returns {any} - Valor computado
 */
export function processarCampoComputado(campo, formValues) {
  if (!campo.computed) {
    return campo.value || '';
  }

  const { dependsOn, format, options } = campo.computed;
  const dependsOnValue = dependsOn ? formValues[dependsOn] : null;
  
  return formatarCampoComputado(format, dependsOnValue, options);
}

/**
 * Identifica todos os campos computados em um formulário
 * @param {array} campos - Array de campos do formulário
 * @returns {array} - Array de campos computados com suas dependências
 */
export function identificarCamposComputados(campos) {
  return campos.filter(campo => campo.computed && campo.computed.dependsOn);
}

/**
 * Cria um mapa de dependências entre campos
 * @param {array} campos - Array de campos do formulário
 * @returns {object} - Mapa de dependências { campoName: [camposQueDependemDele] }
 */
export function criarMapaDependencias(campos) {
  const mapa = {};
  
  campos.forEach(campo => {
    if (campo.computed && campo.computed.dependsOn) {
      const dependsOn = campo.computed.dependsOn;
      
      if (!mapa[dependsOn]) {
        mapa[dependsOn] = [];
      }
      
      mapa[dependsOn].push(campo.name);
    }
  });
  
  return mapa;
}

export default {
  formatarCampoComputado,
  processarCampoComputado,
  identificarCamposComputados,
  criarMapaDependencias,
  gerarSenhaAleatoria,
  gerarCodigoComPrefixoTimestamp,
  gerarDataHoraBrasil,
  gerarUUID
};
